<template>
  <div class="summary-section" :data-aos="isInternalView ? '' : 'fade-up'" data-aos-once="true">
    <v-container>
      <v-expansion-panels v-model="panel" dark accordion multiple>
        <v-expansion-panel expand>
          <v-expansion-panel-header>
            <v-app-bar dark color="#212121">
              <v-toolbar-title style="text-align:center;">Post Analysis Summary</v-toolbar-title>
              <v-spacer></v-spacer>
              <v-menu
                :close-on-content-click="false"
                bottom
                left
                v-if="showRequiredActions()"
              >
                <template v-slot:activator="{ on, attrs }">
                  <v-btn
                    icon
                    color="primary"
                    v-bind="attrs"
                    v-on="on"
                    @click="openColSettingsModal"
                  >
                    <i class="fas fa-cog fa-lg" data-toggle="tooltip" data-placement="top" title="Settings"></i>
                  </v-btn>
                </template>
              </v-menu>              
              <v-btn
                v-if="showRequiredActions()"
                icon
                color="primary"
                @click="downloadSummary"
              >
                <i class="fas fa-download fa-lg" data-toggle="tooltip" data-placement="top" title="Download"></i>
              </v-btn>
              <v-btn
                v-if="isInternalView && showRequiredActions()"
                icon
                color="primary"
                @click.stop="$emit('open-analysis')"
              >
                <i class="fas fa-magic fa-lg" data-toggle="tooltip" data-placement="top" title="AI Analysis"></i>
              </v-btn>
            </v-app-bar>
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-card
              class="mx-auto"
              outlined
              color="#000000"
            >
              <v-card-text>
                <div id="post-analysis-summary">
                  <!-- Group Filter Buttons (right aligned, no "All" button) -->
                  <div v-if="groups.length > 0" class="group-filter-btns-right">
                    <div class="group-btns">
                      <v-btn
                        v-for="group in groups"
                        :key="group.groupID"
                        :color="selectedGroup === group.groupID ? 'orange' : '#fff'"
                        :class="['group-btn', { 'active-btn': selectedGroup === group.groupID }]"
                        @click="selectedGroup = selectedGroup === group.groupID ? null : group.groupID"
                        depressed
                        small
                        style="margin-right:8px; color: #212121;"
                      >
                        {{ group.groupName || group.groupID }}
                      </v-btn>
                    </div>
                  </div>
                  <div class="text-right mb-2">
                    <!-- Save and Cancel buttons for drag-and-drop reordering -->
                    <v-btn
                      @click.native.stop
                      @click="cancelPostOrder"
                      color="warning"
                      dark
                      v-if="editPostOrder"
                      class="mr-2"
                      small
                    >
                      Cancel
                    </v-btn>

                    <v-btn
                      @click.native.stop
                      @click="savePostOrder"
                      color="success"
                      dark
                      v-if="editPostOrder"
                      class="mr-2"
                      small
                    >
                      <v-icon small>mdi-content-save</v-icon> Save Changes
                    </v-btn>
                  </div>
                  <v-data-table
                    dark
                    :headers="computedHeaders"
                    :items="filteredPosts"
                    item-key="key"
                    :items-per-page="15"
                    :page.sync="page"
                    class="elevation-15"
                    :key="dataTableKey"
                    v-sortable-data-table
                    @sorted="handleDataTableDragDrop"
                  >
                    <template v-for="(h, index) in computedHeaders" v-slot:[`header.${h.value}`]="{ header }">
                      <legend-icon :key="index" :header="h" />
                    </template>
                    <template v-slot:item.name="{ item }">
                      <div class="d-flex align-center">
                        <v-icon
                          small
                          class="move-cursor"
                        >
                          mdi-arrow-all
                        </v-icon>
                        <v-btn
                          v-if="isInternalView"
                          class="btn-reload"
                          text
                          x-small
                          color="warning"
                          @click="refreshReportInf(item.id)"
                        >
                          <v-icon dark small>
                            mdi-reload
                          </v-icon>
                        </v-btn>
                        <a class="caption" style="color:#fff" :href="profileUrl(item)" target="_blank" rel="noopener noreferrer">@{{item.profileHandle || item.name}}</a>
                      </div>
                    </template>
                    <template v-slot:[`item.url`]="{ item }">
                      <td class="text-left">
                        <v-btn
                          x-small
                          text
                          color="secondary"
                          elevation="0"
                          :href="item.url"
                          :target="'_blank'"
                        >
                          View
                        </v-btn>
                      </td>
                    </template>
                    <template v-slot:item.outreach="{ item }">
                      {{item.outreach === 0 ? '-' : item.outreach | thousandSeprator}}
                    </template>
                    <template v-slot:item.likes="{ item }">
                      {{item.likes === 0 ? '-' : item.likes | thousandSeprator}}
                    </template>
                    <template v-slot:item.comments="{ item }">
                      {{item.comments === 0 ? '-' : item.comments | thousandSeprator}}
                    </template>
                    <template v-slot:item.saved="{ item }">
                      {{item.saved === 0 ? '-' : item.saved | thousandSeprator}}
                    </template>
                    <template v-slot:item.shares="{ item }">
                      {{item.shares === 0 ? '-' : item.shares}}
                    </template>
                    <template v-slot:item.views="{ item }">
                      {{item.views === 0 ? '-' : item.views}}
                    </template>
                    <template v-slot:item.reach="{ item }">
                      {{item.reach === 0 ? '-' : item.reach}}
                    </template>
                    <template v-slot:item.impressions="{ item }">
                      {{item.impressions === 0 ? '-' : item.impressions}}
                    </template>
                    <template v-slot:item.new_engagement_rate="{ item }">
                      <div>
                        {{item.new_engagement_rate === 0 ? '0' : item.new_engagement_rate}} %
                      </div>
                    </template>
                    <template v-slot:item.new_engagement_rate_view="{ item }">
                      <div>
                        {{item.new_engagement_rate_view === 0 ? '0' : item.new_engagement_rate_view}} %
                      </div>
                    </template>
                    <template v-slot:item.engagement_rate_reach="{ item }">
                      <div>
                        {{item.engagement_rate_reach === 0 ? '0' : item.engagement_rate_reach}} %
                      </div>
                    </template>
                    <template v-slot:item.engagement_rate_sd="{ item }">
                      <div v-if="item.engagement_rate_sd && item.engagement_rate_sd !== '0.00'">
                        {{item.engagement_rate_sd === null ? '-' : item.engagement_rate_sd + '%'}}
                      </div>
                      <div v-else>
                        <v-icon small data-toggle="tooltip" data-placement="top" title="Hidden">mdi-eye-off</v-icon>
                      </div>
                    </template>
                    <template v-slot:item.median_engagement_rate="{ item }">
                      {{item.median_engagement_rate === null ? '-' : item.median_engagement_rate + '%'}}
                    </template>
                    <template v-slot:item.avg_likes="{ item }">
                      {{item.avg_likes === null ? '-' : item.avg_likes | thousandSeprator}}
                    </template>
                    <template v-slot:item.avg_comments="{ item }">
                      {{item.avg_comments === null ? '-' : item.avg_comments | thousandSeprator}}
                    </template>
                    <template v-slot:item.avg_views="{ item }">
                      {{item.avg_views === null ? '-' : item.avg_views | thousandSeprator}}
                    </template>
                    <template slot="body.append">
                      <tr style="font-weight:500;">
                        <td><strong>TOTAL</strong></td>
                        <template v-for="header in computedHeaders">
                          <td :key="header.value" v-if="header.value !== 'name'">
                            <strong>
                              <template v-if="header.value === 'live_date'">-</template>
                              <template v-else-if="header.value === 'url'">-</template>
                              <template v-else-if="header.value === 'outreach'">{{ totalOutreach === 0 ? '-' : totalOutreach | thousandSeprator }}</template>
                              <template v-else-if="header.value === 'reach'">{{ totalReach === 0 ? '-' : totalReach | thousandSeprator }}</template>
                              <template v-else-if="header.value === 'impressions'">{{ totalImpressions === 0 ? '-' : totalImpressions | thousandSeprator }}</template>
                              <template v-else-if="header.value === 'likes'">{{ totalLikes === 0 ? '-' : totalLikes | thousandSeprator }}</template>
                              <template v-else-if="header.value === 'shares'">{{ totalShares === 0 ? '-' : totalShares | thousandSeprator }}</template>
                              <template v-else-if="header.value === 'comments'">{{ totalComments === 0 ? '-' : totalComments | thousandSeprator }}</template>
                              <template v-else-if="header.value === 'saved'">{{ totalSaved === 0 ? '-' : totalSaved | thousandSeprator }}</template>
                              <template v-else-if="header.value === 'interactions'">{{ totalInteractions === 0 ? '-' : totalInteractions | thousandSeprator }}</template>
                              <template v-else-if="header.value === 'new_engagement_rate'">{{ totalEngagementRate === 0 ? '-' : totalEngagementRate }} %</template>
                              <template v-else-if="header.value === 'engagement_rate_reach'">{{ totalEngagementRateReach === 0 ? '-' : totalEngagementRateReach }} %</template>
                              <template v-else-if="header.value === 'engagement_rate_sd'">-</template>
                              <template v-else-if="header.value === 'median_engagement_rate'">-</template>
                              <template v-else-if="header.value === 'avg_likes'">-</template>
                              <template v-else-if="header.value === 'avg_comments'">-</template>
                              <template v-else-if="header.value === 'avg_views'">-</template>
                            </strong>
                          </td>
                        </template>
                      </tr>
                    </template>
                  </v-data-table>
                </div>
                <!-- <legend-section
                  :rightSections="postLegends"
                ></legend-section> -->
              </v-card-text>
            </v-card>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
    </v-container>
    <table-cols-show-hide-modal
      v-model="colSettingsDialog"
      title="Post Analysis Summary"
      :items="tableColsItems"
      @updated="updatePostAnalysisStatus"
      :isSort="true"
    />
  </div>
</template>

<style lang="css" scoped>
.theme--dark.v-data-table > .v-data-table__wrapper > table > thead > tr:last-child > th {
  border: thin solid rgba(255, 255, 255, 0.12) !important;
}
.theme--dark.v-data-table > .v-data-table__wrapper > table > tbody > tr:hover {
  cursor: move;
}
.move-cursor {
  cursor: move;
  float: left;
}
.group-filter-btns-right {
  display: flex;
  justify-content: flex-end;
  align-items: center;
  margin-bottom: 16px;
}
.group-btns {
  display: flex;
  align-items: center;
}
.group-btn {
  min-width: 48px;
  font-weight: 500;
  background: #fff !important;
  border-radius: 8px !important;
  box-shadow: none !important;
  color: #212121 !important;
  transition: background 0.2s, color 0.2s;
}
.active-btn {
  background: orange !important;
  color: #fff !important;
}
</style>

<script>
import { getReportProfileUrl } from '@/utils/reportPlatform'
/* eslint-disable */
import html2canvas from 'html2canvas';
import LegendSection from './Legend'
import LegendIcon from './LegendIcon'
import campaignAPIs from '../views/Campaign/campaignAPIs'
import campaignHelper from '../views/Campaign/campaignHelper';
import TableColsShowHideModal from '@/components/scoped/custom/TableColsShowHideModal'
import config from '@/config';
export default {
  name: 'rpt_post_summary',
  props: ['posts', 'campaign', 'defaultCols', 'isInternalView'],
  components: {
    LegendSection,
    LegendIcon,
    TableColsShowHideModal
  },
  data () {
    return {
      page: 1,
      sortBy: 'live_date',
      sortDesc: true,
      dataTableKey: 0,
      disableSaveArrange: true,
      lineUpItems: [],
      panel: [0, 1],
      postLegends: [],
      selectedGroup: null,
      colSettingsDialog: false,
      tableColsItems: [],
      editPostOrder: false,
      originalPosts: [],
      showHideFields: [
        { name: 'liveDate', label: 'Live Date' },
        { name: 'url', label: 'Post URL' },
        { name: 'outreach', label: 'Audience Size' },
        { name: 'showReach', label: 'Accounts Reached' },
        { name: 'showImpressions', label: 'Impressions' },
        { name: 'likes', label: 'Likes' },
        { name: 'showShares', label: 'Shares' },
        { name: 'comments', label: 'Comments' },
        { name: 'showBookmarks', label: 'Bookmarks' }, 
        { name: 'showInteractions', label: 'Interactions' },
        { name: 'showEngagement', label: 'Engagement Rate by Audience Size' },
        { name: 'showEngRateReach', label: 'Engagement Rate by Reach' },
        { name: 'showAvgEngagement', label: 'Avg. Engagement Rate' },
        { name: 'showMediaEngagement', label: 'Median Engagement Rate' },
        { name: 'showAvgLikes', label: 'Average Likes' },
        { name: 'showAvgComments', label: 'Average Comments' },
        { name: 'showAvgViews', label: 'Average Views' }
      ],
      headers: [
        {
          text: 'Influencer',
          align: 'start',
          sortable: false,
          value: 'name'
        },
        {
          text: 'Live Date',
          value: 'live_date',
          legendText: 'Live Date'
        },
        {
          text: 'Post URL',
          value: 'url',
          legendText: 'Post URL'
        },
        {
          text: 'Audience Size',
          value: 'outreach',
          legendText: 'The number of followers the creator has at the time where the post was made '
        },
        {
          text: 'Accounts Reached',
          value: 'reach',
          legendText: 'The total number of unique users who viewed the content'
        },
        {
          text: 'Impressions',
          value: 'impressions',
          legendText: `The total number of times the content is displayed on an user's feed`
        },
        {
          text: 'Likes',
          value: 'likes',
          legendText: 'The number of times users have tapped the “like” button on a post.'
        },
        {
          text: 'Shares',
          value: 'shares',
          legendText: 'The number of times users have reposted / shared a post.'
        },
        {
          text: 'Comments',
          value: 'comments',
          legendText: 'The number of written responses to a post.'
        },
        {
          text: 'Bookmarks',
          value: 'saved',
          legendText: 'The number of times users have saved a post to their “Saved” folder.'
        },
        {
          text: 'Interactions',
          value: 'interactions',
          legendText: 'The sum of likes, comments, saves, shares'
        },
        // {
        //   text: 'Views',
        //   value: 'views',
        //   legendText: 'The number of times users have viewed a video for 3 seconds or more, not inclusive of looped views.'
        // },
        {
          text: 'Engagement Rate by Audience Size',
          value: 'new_engagement_rate',
          legendText: 'The total number of interactions (users who liked, commented, saved or shared the post) divided by the audience size numbers'
        },
        // {
        //   text: 'Play Rate (by Audience Size)',
        //   value: 'new_engagement_rate_view',
        //   legendText: 'The total number of views divided by the audience size numbers'
        // },
        {
          text: 'Engagement Rate by Reach',
          value: 'engagement_rate_reach',
          legendText: 'The total number of interactions (users who liked, commented, saved or shared the post) divided by reach'
        },
        {
          text: 'Avg. Engagement Rate',
          value: 'engagement_rate_sd',
          legendText: 'The average sum of engagement rate on the last 30 posts'
        },
        {
          text: 'Median Engagement Rate',
          value: 'median_engagement_rate',
          legendText: 'The middle engagement rate amongst the sorted engagement rate of all the creator’ within same tier comparison.'
        },
        {
          text: 'Average Likes',
          value: 'avg_likes',
          legendText: 'Average likes over the last 30 posts'
        },
        {
          text: 'Average Comments',
          value: 'avg_comments',
          legendText: 'Average comments over the last 30 posts'
        },
        {
          text: 'Average Views',
          value: 'avg_views',
          legendText: 'Average views over the last 30 posts'
        }
      ],
      postRightSection: [
        {
          title: 'Live Date',
          description: 'Live Date'
        },
        {
          title: 'Post URL',
          description: 'Post URL'
        },
        {
          title: 'Likes',
          description: 'Number of likes'
        },
        {
          title: 'Comments',
          description: 'Number of comments'
        },
        {
          title: 'Bookmarks',
          description: 'Number of bookmarks'
        },
        {
          title: 'Shares',
          description: 'Number of shares'
        },
        {
          title: 'Engagement Rate',
          description: 'Followers divided by total interactions for this campaign'
        },
        {
          title: 'Views',
          description: 'Number of views'
        },
        {
          title: 'Reach',
          description: 'Number of unique user that seen your content'
        },
        {
          title: 'Impressions',
          description: 'Total number of times content have been shown to users'
        },
        {
          title: 'Average Engagement Rate',
          description: 'Average engagement for all advertorials and organic content'
        },
        {
          title: 'Median Engagement Rate',
          description: 'Engagement Rate within same tier comparison'
        },
        {
          title: 'Average Likes',
          description: 'Average likes over the last 30 posts'
        },
        {
          title: 'Average Comments',
          description: 'Average comments over the last 30 posts'
        },
        {
          title: 'Average Views',
          description: 'Average views over the last 30 posts'
        },
        {
          title: 'Average Reels Plays',
          description: 'Average plays over the last 30 posts'
        }
      ]
    }
  },
  computed: {
    sortedPosts() {
      return this.posts;
    },
    groups() {
      // Extract unique groupID/groupName from posts and their reports
      const groupMap = new Map();
      this.sortedPosts.forEach(post => {
        if (post.groupID && post.groupName) {
          groupMap.set(post.groupID, post.groupName);
        }
        if (Array.isArray(post.reports)) {
          post.reports.forEach(report => {
            if (report.groupID && report.groupName) {
              groupMap.set(report.groupID, report.groupName);
            }
          });
        }
      });
      return Array.from(groupMap, ([groupID, groupName]) => ({
        groupID,
        groupName
      }));
    },
    filteredPosts() {
      if (!this.selectedGroup) return this.sortedPosts;
      return this.sortedPosts.filter(post => {
        if (post.groupID === this.selectedGroup) return true;
        if (Array.isArray(post.reports)) {
          return post.reports.some(report => report.groupID === this.selectedGroup);
        }
        return false;
      });
    },
    computedHeaders() {
      const status = this.campaign.postAnalysisStatus || {};
      const finalHeaders = [];

      // 1. Add the "Influencer" column first (always fixed)
      const nameHeader = this.headers.find(h => h.value === 'name');
      if (nameHeader) {
        finalHeaders.push(nameHeader);
      }

      // 2. Iterate through the keys of postAnalysisStatus to determine order and visibility
      for (const statusKey in status) {
        if (Object.prototype.hasOwnProperty.call(status, statusKey)) {
          const isVisible = status[statusKey];
          if (isVisible) {
            // Find the corresponding header from `headers` using `showHideFields` as a map
            const field = this.showHideFields.find(f => f.name === statusKey);
            if (field) {
              const header = this.headers.find(h => h.text === field.label);
              if (header) {
                finalHeaders.push(header);
              }
            } else {
              // Fallback for keys that might directly match header values (like 'outreach', 'likes', 'comments')
              const header = this.headers.find(h => h.value === statusKey);
              if (header) {
                finalHeaders.push(header);
              }
            }
          }
        }
      }
      return finalHeaders;
    },
    totalOutreach() {
      let outreach = 0;
      this.filteredPosts.map(item => {
        outreach += item.outreach ? Number(item.outreach) : 0
      })
      return outreach
    },
    totalEngagement() {
      let engagement = 0;
      this.filteredPosts.map(item => {
        engagement += item.engagement ? item.engagement : 0
      })
      return engagement
    },
    totalLikes() {
      let likes = 0;
      this.filteredPosts.map(item => {
        likes += item.likes ? item.likes : 0
      })
      return likes
    },
    totalComments() {
      let comments = 0;
      this.filteredPosts.map(item => {
        comments += item.comments ? item.comments : 0
      })
      return comments
    },
    totalSaved() {
      let saved = 0;
      this.filteredPosts.map(item => {
        saved += item.saved ? item.saved : 0
      })
      return saved
    },
    totalShares() {
      let shares = 0;
      this.filteredPosts.map(item => {
        shares += item.shares ? item.shares : 0
      })
      return shares
    },
    totalViews() {
      let views = 0;
      this.filteredPosts.map(item => {
        views += item.views ? item.views : 0
      })
      return views.toFixed(2)
    },
    totalInteractions() {
      let interactions = 0;
      this.filteredPosts.map(item => {
        interactions += item.interactions ? item.interactions : 0
      })
      return interactions
    },
    totalReach() {
      let reach = 0;
      this.filteredPosts.map(item => {
        reach += item.reach ? Number(item.reach) : 0
      })
      return reach
    },
    totalImpressions() {
      let impressions = 0;
      this.filteredPosts.map(item => {
        impressions += item.impressions ? item.impressions : 0
      })
      return impressions
    },
    totalEngagementRate() {
      let totalEng = 0;
      this.filteredPosts.map(item => {
        totalEng += item.new_engagement_rate ? item.new_engagement_rate : 0
      })
      return this.filteredPosts.length ? ((totalEng / this.filteredPosts.length)).toFixed(2) : 0
    },
    totalEngagementRateView() {
      let totalEng = 0;
      this.filteredPosts.map(item => {
        totalEng += item.new_engagement_rate_view ? item.new_engagement_rate_view : 0
      })
      return this.filteredPosts.length ? ((totalEng / this.filteredPosts.length)).toFixed(2) : 0
    },
    totalEngagementRateReach() {
      let totalEng = 0;
      this.filteredPosts.map(item => {
        totalEng += item.engagement_rate_reach ? item.engagement_rate_reach : 0
      })
      return this.filteredPosts.length ? ((totalEng / this.filteredPosts.length)).toFixed(2) : 0
    },
    currentUserRole: function() {
      return Number(this.$store.state.user.role);
    }
  },
  mounted() {
    this.updateLegend();
  },
  methods: {
    profileUrl(item) {
      return getReportProfileUrl(item, 'instagram')
    },
    customSort: function(items, index, isDesc) {
      items.sort((a, b) => {
        if (!(isNaN(a[index[0]]))) {
          if (!isDesc[0]) {
            return (a[index[0]] - b[index[0]]);
          } else {
            return (b[index[0]] - a[index[0]]);
          }
        } else {
          if (!isDesc[0]) {
            return (a[index[0]] < b[index[0]]) ? -1 : 1;
          } else {
            return (b[index[0]] < a[index[0]]) ? -1 : 1;
          }
        }
      })
      this.lineUpItems = Object.assign([], items)
      this.disableSaveArrange = false
      return items;
    },
    handleDataTableDragDrop(event, items) {
      // check and save drag n drop update for table items/ grouped items
      if (items.length === 0 || !this.showRequiredActions()) {
        return;
      }
      
      // Store original posts if this is the first edit
      if (!this.editPostOrder) {
        this.originalPosts = JSON.parse(JSON.stringify(this.posts));
      }
  
      const itemsPerPage = 15;
      const oldIndex = ((this.page - 1) * itemsPerPage) + event.oldIndex;
      const newIndex = ((this.page - 1) * itemsPerPage) + event.newIndex;
      let tmpPosts = []
      let groupID = items[0].groupID
      if (groupID) {
        let group = this.sortedPosts.find(p => p.id === groupID)
        if (group) {
          tmpPosts = group.reports
        }
      } else {
        tmpPosts = Object.assign([], this.sortedPosts)
      }
      const movedItem = tmpPosts.splice(oldIndex, 1)[0]
      tmpPosts.splice(newIndex, 0, movedItem)
      if (groupID) {
        this.posts = this.sortedPosts.map(p => {
          if (p.id === groupID) {
            p.reports = tmpPosts.map(tmp => tmp)
          }
          return p
        })
      } else {
        this.posts = tmpPosts.map(tmp => tmp)
      }
      this.lineUpItems = []
      this.lineUpItems = Object.assign([], tmpPosts)
      this.disableSaveArrange = false
      this.editPostOrder = true
    },
    savePostOrder() {
      this.updateLineUp();
      this.editPostOrder = false;
      this.originalPosts = [];
    },
    cancelPostOrder() {
      // Revert to original posts order
      this.posts = JSON.parse(JSON.stringify(this.originalPosts));
      this.lineUpItems = [];
      this.disableSaveArrange = true;
      this.editPostOrder = false;
      this.originalPosts = [];
    },
    updateLegend() {
      let filteredLegend = this.postRightSection.filter(element => {
        let postAnalysisStatus = this.campaign.postAnalysisStatus
        if (!postAnalysisStatus.showEngagement && element.title === 'Engagement Rate') {
          return false
        }
        if (!postAnalysisStatus.showAvgEngagement && element.title === 'Average Engagement Rate') {
          return false
        }
        if (!postAnalysisStatus.showMediaEngagement && element.title === 'Median Engagement Rate') {
          return false
        }
        if (!postAnalysisStatus.showAvgLikes && element.title === 'Average Likes') {
          return false
        }
        if (!postAnalysisStatus.showAvgComments && element.title === 'Average Comments') {
          return false
        }
        if (!postAnalysisStatus.showAvgViews && element.title === 'Average Views') {
          return false
        }
        if (!postAnalysisStatus.likes && element.title === 'Likes') {
          return false
        }
        if (!postAnalysisStatus.comments && element.title === 'Comments') {
          return false
        }
        if (!postAnalysisStatus.showBookmarks && element.title === 'Bookmarks') {
          return false
        }
        if (!postAnalysisStatus.showShares && element.title === 'Shares') {
          return false
        }
        if (!postAnalysisStatus.showViews && element.title === 'Views') {
          return false
        }
        if (!postAnalysisStatus.showReach && element.title === 'Reach') {
          return false
        }
        if (!postAnalysisStatus.showImpressions && element.title === 'Impressions') {
          return false
        }
        if (!postAnalysisStatus.liveDate && element.title === 'Live Date') {
          return false
        }
        if (!postAnalysisStatus.url && element.title === 'Live Date') {
          return false
        }
        return true
      });
      this.postLegends = filteredLegend
    },
    async updatePostAnalysisStatus(items) {
      // let columns = this.campaign.postAnalysisStatus || {}
      // items.map(item => {
      //   columns[item.key] = item.value
      // })
      // let data = [Object.assign({}, {
      //   id: this.campaign.id,
      //   postAnalysisStatus: columns
      // })]
      // await this.$store.dispatch('campaign/updateCampaignByIds', data)
      
      // this.colSettingsDialog = false
      await this.updateColumnsStatus(items, 'postAnalysisStatus', 'colSettingsDialog');
    },
    showRequiredActions: function() {
      if (!this.$store.state.token || this.$store.state.token === 'null' || !this.$store.state.user) {
        return false
      } else {
        return config.userRoles.CLIENT !== this.currentUserRole;
      }
    },
    openColSettingsModal() {
      // this.tableColsItems = []
      // let tmpStatuses = {}
      // if (this.campaign.postAnalysisStatus) {
      //   tmpStatuses = Object.assign({}, this.campaign.postAnalysisStatus)
      // } else {
      //   this.showHideFields.map(header => {
      //     tmpStatuses[header.name] = true
      //   })
      // }
      // for (const [key, value] of Object.entries(tmpStatuses)) {
      //   let header = this.showHideFields.find(item => item.name === key)
      //   if (header) {
      //     this.tableColsItems.push({
      //       title: header.label,
      //       key,
      //       value
      //     })
      //   }
      // }
      this.initializePostAnalysisTableCols();
      this.colSettingsDialog = true
    },
    // --- SPECIFIC INITIALIZERS AND OPENERS (USING campaignData.*Status) ---
    initializePostAnalysisTableCols() {
        this.tableColsItems = campaignHelper.getOrderedColumns(
          this.showHideFields,
          this.campaign.postAnalysisStatus,
          this.defaultCols
        );
    },
    // --- GENERIC HELPER FOR UPDATING COLUMN STATUS (SAVES BACK TO FLAT OBJECT AT ROOT LEVEL) ---
    async updateColumnsStatus(items, campaignDataProp, dialogProp) {
        let newStatusObject = {};
        
        // 1. Create the new flat object. Order is implicitly captured by key insertion order.
        items.forEach((item) => {
            newStatusObject[item.key] = item.value;
        });
        
        // 2. Construct the API payload based on your example: { id, statusObject }
        let data = [
            Object.assign({}, {
                id: this.campaign.id,
                [campaignDataProp]: newStatusObject // Directly map status object to campaign property name
            })
        ];
        
        try {
            // NOTE: Using campaign/updateCampaignByIds expects the endpoint to handle 
            // updating properties directly from the root of the data object.
            await this.$store.dispatch('campaign/updateCampaignByIds', data);
            
            // 3. Update the local campaignData property directly
            this.campaign[campaignDataProp] = newStatusObject;
            this[dialogProp] = false;
        } catch (error) {
            console.error('Error updating campaign settings:', error);
        }
    },
    showPlayTime(time) {
      if (time) {
        return campaignHelper.showTikTokPlayTime(time)
      }
      return ''
    },
    downloadSummary() {
      const element = document.getElementById('post-analysis-summary');
      html2canvas(element).then(canvas => {
        const link = document.createElement('a');
        link.href = canvas.toDataURL('image/png');
        link.download = 'post-analysis-summary.png';
        link.click();
      });
    },
    refreshReportInf(id) {
      this.$emit('refreshReportInf', id)
    },
    async updateLineUp() {
      await this.lineUpItems.map((item, index) => {
        let data = {}
        if (item.reportGroup) {
          data.groupID = item.id
          data.lineUpIndex = index
          data.posts = item.reports.map((element, rindex) => {
            return Object.assign({}, { id: element.id, lineUpIndex: rindex })
          })
        } else {
          data = {
            reportID: item.id,
            campaignID: item.campaignID,
            lineUpIndex: index
          }
        }
        campaignAPIs.updateLineUpIndex(data)
      })
    }
  }
}
</script>



// WEBPACK FOOTER //
// PostSummary.vue?3c2272b4